import React, { useState, useMemo } from "react";
import CommonCollapsible from "../common/CommonCollapsible";
import { MdClose, MdKeyboardArrowDown } from "react-icons/md";
import Modal1 from "../DIsplay/Modal/Modal1";
import {
  buildFareRuleSectionBody,
  capitalizePolicySentence,
} from "../../data/flight/order/getChangeFareRuleInsight";

function PolicyBlock({ before, after, fee }) {
  return (
    <div className="w-full flex flex-col gap-1.5">
      {before ? (
        <div>
          <p className="text-[11px] font-semibold text-gray-500">
            Before departure
          </p>
          <p className="text-sm whitespace-pre-line">{before}</p>
        </div>
      ) : null}
      {after ? (
        <div>
          <p className="text-[11px] font-semibold text-gray-500">
            After departure
          </p>
          <p className="text-sm whitespace-pre-line">{after}</p>
        </div>
      ) : null}
      {fee ? (
        <div>
          <p className="text-[11px] font-semibold text-gray-500">Fee</p>
          <p className="text-sm whitespace-pre-line">{fee}</p>
        </div>
      ) : null}
    </div>
  );
}

export const FareRulesModal = ({
  setOpen: setModalOpen,
  open: modalOpen,
  fareRules,
  formattedFareRules,
  loading = false,
  error = "",
}) => {
  const [mode, setMode] = useState("formatted");
  const rawFareRulesText =
    typeof fareRules === "string" && fareRules.trim() ? fareRules : "";

  const summary = capitalizePolicySentence(
    formattedFareRules?.fareruleSummary?.fareruleSummary || "",
  );
  const otherDetails = capitalizePolicySentence(
    formattedFareRules?.otherNecessaryDetails?.otherNecessaryDetails || "",
  );

  const [sectionOpen, setSectionOpen] = useState({
    refund: false,
    voidRule: false,
    reissue: false,
    noShow: false,
    otherDetails: false,
  });

  const reissueBody = useMemo(
    () =>
      buildFareRuleSectionBody(formattedFareRules?.reissue, {
        beforeKey: "reissuePolicyBeforeDeparture",
        afterKey: "reissuePolicyAfterDeparture",
        feeKey: "reissueFee",
      }),
    [formattedFareRules?.reissue],
  );
  const refundBody = useMemo(
    () =>
      buildFareRuleSectionBody(formattedFareRules?.refund, {
        beforeKey: "refundPolicyBeforeDeparture",
        afterKey: "refundPolicyAfterDeparture",
        feeKey: "refundFee",
      }),
    [formattedFareRules?.refund],
  );
  const noShowBody = useMemo(
    () =>
      buildFareRuleSectionBody(formattedFareRules?.noShow, {
        beforeKey: "noShowPolicyBeforeDeparture",
        afterKey: "noShowPolicyAfterDeparture",
        feeKey: "noShowFee",
      }),
    [formattedFareRules?.noShow],
  );
  const voidBody = useMemo(
    () =>
      buildFareRuleSectionBody(formattedFareRules?.void, {
        beforeKey: "voidPolicyBeforeDeparture",
        afterKey: "voidPolicyAfterDeparture",
      }),
    [formattedFareRules?.void],
  );

  const reissue = formattedFareRules?.reissue || {};
  const refund = formattedFareRules?.refund || {};
  const noShow = formattedFareRules?.noShow || {};
  const voidRule = formattedFareRules?.void || {};

  const hasFormattedContent = useMemo(() => {
    return Boolean(
      summary ||
        otherDetails ||
        reissueBody ||
        refundBody ||
        noShowBody ||
        voidBody,
    );
  }, [summary, otherDetails, reissueBody, refundBody, noShowBody, voidBody]);

  const renderBadge = (label, tone = "green") => {
    const toneClasses =
      tone === "green"
        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
        : tone === "red"
          ? "bg-red-50 text-red-700 border-red-200"
          : "bg-gray-50 text-gray-700 border-gray-200";

    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${toneClasses}`}
      >
        {label}
      </span>
    );
  };

  const toggleSection = (key) => {
    setSectionOpen((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <Modal1 open={modalOpen} setOpen={setModalOpen}>
      <div className="w-full max-w-[700px] py-4 flex flex-col gap-4 ">
        <div className="w-full flex items-center justify-between px-2 lg:px-4">
          <span className="">Fare rules</span>
          <button type="button" onClick={() => setModalOpen(false)}>
            <MdClose className="text-2xl" />
          </button>
        </div>
        <div className="w-full flex flex-col px-2 lg:px-4 gap-4 max-h-[70vh] overflow-y-auto scroll-hide">
          {loading ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-sm text-gray-600">
              <div className="primaryLoader" />
              <span>Loading fare rules…</span>
            </div>
          ) : error && !hasFormattedContent && !rawFareRulesText ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-4 text-sm text-amber-900">
              {error}
            </div>
          ) : (
            <>
          <div className="flex items-center justify-between mb-2">
            <div className="inline-flex rounded-full bg-gray-100 p-1 text-xs">
              <button
                type="button"
                onClick={() => setMode("formatted")}
                className={`px-3 py-1 rounded-full transition ${
                  mode === "formatted"
                    ? "bg-white shadow-sm text-gray-900"
                    : "text-gray-500"
                }`}
                disabled={!hasFormattedContent}
              >
                Summary
              </button>
              <button
                type="button"
                onClick={() => setMode("raw")}
                className={`px-3 py-1 rounded-full transition ${
                  mode === "raw"
                    ? "bg-white shadow-sm text-gray-900"
                    : "text-gray-500"
                }`}
                disabled={!rawFareRulesText}
              >
                Airline full text
              </button>
            </div>

            {!hasFormattedContent && formattedFareRules && (
              <span className="text-[11px] text-gray-500">
                Limited structured details – raw text may have more.
              </span>
            )}
          </div>

          {mode === "formatted" && (
            <div className="space-y-4 text-sm text-gray-800">
              {!hasFormattedContent && (
                <div className="text-xs text-gray-500">
                  No structured fare rule summary is available.
                  {rawFareRulesText ? (
                    <>
                      {" "}
                      Try the{" "}
                      <button
                        type="button"
                        onClick={() => setMode("raw")}
                        className="text-primary1 underline"
                      >
                        airline full text
                      </button>
                      .
                    </>
                  ) : null}
                </div>
              )}

              {summary ? (
                <section className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                  <h3 className="text-xs font-semibold text-gray-600 uppercase mb-1.5">
                    Overview
                  </h3>
                  <p className="text-sm leading-relaxed whitespace-pre-line">
                    {summary}
                  </p>
                </section>
              ) : null}

              {reissueBody ? (
                <section className="bg-white rounded-lg p-3 border border-gray-100">
                  <div className="flex items-center justify-between mb-1.5">
                    <button
                      type="button"
                      onClick={() => toggleSection("reissue")}
                      className="flex items-center gap-1 flex-1 text-left"
                    >
                      <h3 className="text-xs font-semibold text-gray-600 uppercase">
                        Changes / Reissue
                      </h3>
                    </button>
                    <div className="flex gap-2 ml-2">
                      {reissue?.isReissuable === true &&
                        renderBadge("Changeable", "green")}
                      {reissue?.isReissuable === false &&
                        renderBadge("Changes not allowed", "red")}
                      {reissueBody.fee
                        ? renderBadge(`Fee: ${reissueBody.fee}`, "gray")
                        : null}
                      <MdKeyboardArrowDown
                        className={`text-gray-500 transition-transform duration-200 ${
                          sectionOpen.reissue ? "rotate-180" : ""
                        }`}
                        size={16}
                      />
                    </div>
                  </div>
                  <CommonCollapsible open={sectionOpen.reissue}>
                    <PolicyBlock {...reissueBody} />
                  </CommonCollapsible>
                </section>
              ) : null}

              {refundBody ? (
                <section className="bg-white rounded-lg p-3 border border-gray-100">
                  <div className="flex items-center justify-between mb-1.5">
                    <button
                      type="button"
                      onClick={() => toggleSection("refund")}
                      className="flex items-center gap-1 flex-1 text-left"
                    >
                      <h3 className="text-xs font-semibold text-gray-600 uppercase">
                        Refunds
                      </h3>
                    </button>
                    <div className="flex gap-2 ml-2">
                      {refund?.isRefundable === true &&
                        renderBadge("Refundable", "green")}
                      {refund?.isRefundable === false &&
                        renderBadge("Non-refundable", "red")}
                      {refundBody.fee
                        ? renderBadge(`Fee: ${refundBody.fee}`, "gray")
                        : null}
                      <MdKeyboardArrowDown
                        className={`text-gray-500 transition-transform duration-200 ${
                          sectionOpen.refund ? "rotate-180" : ""
                        }`}
                        size={16}
                      />
                    </div>
                  </div>
                  <CommonCollapsible open={sectionOpen.refund}>
                    <PolicyBlock {...refundBody} />
                  </CommonCollapsible>
                </section>
              ) : null}

              {noShowBody ? (
                <section className="bg-white rounded-lg p-3 border border-gray-100">
                  <div className="flex items-center justify-between mb-1.5">
                    <button
                      type="button"
                      onClick={() => toggleSection("noShow")}
                      className="flex items-center gap-1 flex-1 text-left"
                    >
                      <h3 className="text-xs font-semibold text-gray-600 uppercase">
                        No-show
                      </h3>
                    </button>
                    <div className="flex gap-2 ml-2">
                      {noShow?.isNoShow === true &&
                        renderBadge("No-show penalties apply", "red")}
                      {noShowBody.fee
                        ? renderBadge(`Fee: ${noShowBody.fee}`, "gray")
                        : null}
                      <MdKeyboardArrowDown
                        className={`text-gray-500 transition-transform duration-200 ${
                          sectionOpen.noShow ? "rotate-180" : ""
                        }`}
                        size={16}
                      />
                    </div>
                  </div>
                  <CommonCollapsible open={sectionOpen.noShow}>
                    <PolicyBlock {...noShowBody} />
                  </CommonCollapsible>
                </section>
              ) : null}

              {voidBody ? (
                <section className="bg-white rounded-lg p-3 border border-gray-100">
                  <div className="flex items-center justify-between mb-1.5">
                    <button
                      type="button"
                      onClick={() => toggleSection("voidRule")}
                      className="flex items-center gap-1 flex-1 text-left"
                    >
                      <h3 className="text-xs font-semibold text-gray-600 uppercase">
                        Void
                      </h3>
                    </button>
                    <div className="flex gap-2 ml-2">
                      {voidRule?.isVoidable === true &&
                        renderBadge("Voidable", "green")}
                      {voidRule?.isVoidable === false &&
                        renderBadge("Not voidable", "red")}
                      <MdKeyboardArrowDown
                        className={`text-gray-500 transition-transform duration-200 ${
                          sectionOpen.voidRule ? "rotate-180" : ""
                        }`}
                        size={16}
                      />
                    </div>
                  </div>
                  <CommonCollapsible open={sectionOpen.voidRule}>
                    <PolicyBlock {...voidBody} />
                  </CommonCollapsible>
                </section>
              ) : null}

              {otherDetails ? (
                <section className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                  <div className="flex items-center justify-between mb-1.5">
                    <button
                      type="button"
                      onClick={() => toggleSection("otherDetails")}
                      className="flex items-center gap-1 flex-1 text-left w-full justify-between"
                    >
                      <h3 className="text-xs font-semibold text-gray-600 uppercase">
                        Other conditions
                      </h3>
                      <MdKeyboardArrowDown
                        className={`text-gray-500 transition-transform duration-200 ${
                          sectionOpen.otherDetails ? "rotate-180" : ""
                        }`}
                        size={16}
                      />
                    </button>
                  </div>
                  <CommonCollapsible open={sectionOpen.otherDetails}>
                    <p className="text-sm whitespace-pre-line leading-relaxed">
                      {otherDetails}
                    </p>
                  </CommonCollapsible>
                </section>
              ) : null}
            </div>
          )}

          {mode === "raw" && (
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
              {rawFareRulesText ? (
                <pre className="whitespace-pre-wrap text-sm text-gray-800 leading-relaxed font-mono">
                  {rawFareRulesText}
                </pre>
              ) : (
                <p className="text-xs text-gray-500">
                  No airline fare rule text is available for this fare.
                </p>
              )}
            </div>
          )}
            </>
          )}
        </div>
      </div>
    </Modal1>
  );
};
